import { CircularProgress, Grid } from '@material-ui/core';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { compose } from 'recompose';
import { OnwardBreadcrumbActive, OnwardBreadcrumbInactive, OnwardBreadcrumbSpacer } from '../Breadcrumbs';
import { OnwardTab, OnwardTabContainer } from '../Tabs';
import Snackbar from '../Snackbar';
import { Context, withContext } from './store';
import { css } from '@emotion/react';
import ExceptionResolutionModal from '@/components/admin/AdminExceptions/modals/ExceptionResolutionModal';
import { colors } from '@/styles';
import ManifestGrid from './ManifestGrid';
import EditManifest from './modals/EditManifest';
import StorePallets from './modals/StorePallets';
import PalletLogs from './modals/PalletLogs';
import ConsolidatePallets from './modals/ConsolidatePallets';
import StagePallets from './modals/StagePallets';
import { EXCEPTION_TYPES } from '@onward-delivery/core';
import ExceptionModal from '@/components/OrderDetailsPage/modals/Exceptions';
import RenamePallet from './modals/RenamePallet';
import PartialReceive from './modals/PartialReceive';
import Unwind from './modals/Unwind';
import Survey from './Survey';
import ActivityLog from './ActivityLog';
import { useClientUser } from '@/hooks';
import { useCarriedSearchTerm } from './ManifestGrid/useCarriedSearchTerm';
import { useGridSeedTerm } from './ManifestGrid/useGridSeedTerm';
import { manifestsListHref } from '@/components/Manifests/utils';

const TABS = {
    DETAILS: 'DETAILS',
    SURVEY: 'SURVEY',
    ACTIVITY_LOG: 'ACTIVITY_LOG',
};

const Manifest = () => {
    const navigate = useNavigate();
    const { state, callbacks, loading } = useContext(Context);
    const { user_id } = useClientUser();
    const [tab, setTab] = useState(TABS.DETAILS);
    // The term this page was opened with, not whatever is typed in the grid's search box later.
    const carriedSearch = useCarriedSearchTerm();
    // A finished query with no manifest (error or not found) also counts, so the grid shows empty rather than a spinner forever.
    const loaded = !!state.manifest.manifest_id || !loading.queryLoading;
    const gridOrders = (state.manifest.orders || []).filter((order) => state.editable || order.shipper_id === user_id);
    // The grid gets the carried term only the first time it shows, and only if it matches something there.
    const {
        ready: gridReady,
        seedTerm,
        clearSeed,
    } = useGridSeedTerm({ carried: carriedSearch, orders: gridOrders, loaded });

    const props = {
        loading,
        callbacks,
    };

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={callbacks.clearNotification}
            />
            <EditManifest manifest={state.editingManifest} {...props} />
            <StorePallets
                pallets={state.storingPallets}
                warehouseId={state.manifest.warehouse_id}
                manifest={state.stagingZoneManifest}
                {...props}
            />
            <PalletLogs pallet={state.palletLog} {...props} />
            <ConsolidatePallets
                pallets={state.consolidatingPallets}
                manifest={state.manifest}
                {...props}
            />
            <RenamePallet pallet={state.renamingPallet} {...props} />
            <PartialReceive target={state.partialReceiveItem} {...props} />
            <Unwind target={state.unwind} {...props} />
            <ExceptionResolutionModal
                {...state.resolveExceptionParams}
                loading={loading.resolveExceptionLoading}
                callbacks={{
                    onClose: () => {
                        callbacks.setResolveExceptionParams({});
                    },
                    onResolve: ({
                        toSubmit,
                        items = [],
                        order = {},
                        exception = {},
                        event = {},
                        isWhReturn = false,
                    }) => {
                        const toRemove = items.map((item) => item.item_id);
                        const { order_id, ...orderUpdates } = order;
                        const { event_id, ...eventUpdates } = event;
                        const { exception_id, ...exceptionUpdates } = exception;

                        return Promise.all([
                            callbacks.resolveException({
                                variables: {
                                    orders: order_id ? [order_id] : [],
                                    events: event_id ? [event_id] : [],
                                    exceptions: exception_id ? [exception_id] : [],
                                    items: toRemove,
                                    order_update: orderUpdates,
                                    event_update: eventUpdates,
                                    exception_update: exceptionUpdates,
                                    include_route_update: false,
                                },
                            }),
                            ...(toSubmit
                                ? [
                                      callbacks.submitOrders({
                                          client_id: toSubmit.shipper_id,
                                          orders: [toSubmit],
                                          filename: undefined,
                                          type: 'MANUAL',
                                      }),
                                  ]
                                : []),
                        ]).then(async ([, resp]) => {
                            if (resp?.data?.jobs?.length > 0) {
                                if (exception.resolution === 'CREATE_RETURN') {
                                    await callbacks.saveAssociatedReturnOrder(order_id, resp.data.jobs[0], isWhReturn);
                                }
                                navigate(`/job/${resp.data.jobs[0]}`);
                            } else {
                                callbacks.setResolveExceptionParams({});
                            }
                        });
                    },
                }}
            />
            <StagePallets pallets={state.stagingPallets} {...props} />
            <ExceptionModal
                {...state.editingException}
                loading={loading.exceptionLoading}
                types={[EXCEPTION_TYPES.DAMAGED, EXCEPTION_TYPES.OVERAGE, EXCEPTION_TYPES.SHORTAGE]}
                callbacks={{
                    onError: (e) => {
                        callbacks.onError(e);
                    },
                    onSubmitCreateAnother: ({ exception }) => {
                        return callbacks.createException(exception);
                    },
                    onSubmit: ({ exception }) => {
                        return callbacks.createException(exception).then(() => {
                            callbacks.editException(null);
                        });
                    },
                    onClose: () => callbacks.editException(null),
                }}
            />
            <Grid
                container
                direction="row"
                css={css`
                    background-color: white;
                    padding: 0 2rem;
                    align-items: center;
                    border-bottom: 1px solid ${colors.greys.border};
                    box-shadow: 0px 1px 3px 0px #3f3f4426;
                    box-shadow: 0px 0px 0px 1px #3f3f440d;
                `}
            >
                <Grid
                    item
                    css={css`
                        flex: 1;
                        display: flex;
                        align-items: center;
                    `}
                >
                    <OnwardBreadcrumbInactive href={manifestsListHref(carriedSearch)}>
                        Manifests
                    </OnwardBreadcrumbInactive>
                    <OnwardBreadcrumbSpacer />
                    <OnwardBreadcrumbActive>
                        {state.manifest.name || `Manifest ${state.manifest.manifest_number}`}
                    </OnwardBreadcrumbActive>
                </Grid>
                <Grid item>
                    <OnwardTabContainer
                        value={tab}
                        onChange={(_, value) => {
                            clearSeed();
                            setTab(value);
                        }}
                    >
                        <OnwardTab label="Details" value={TABS.DETAILS} />
                        <OnwardTab label="Load Feedback Survey" value={TABS.SURVEY} />
                        <OnwardTab label="Activity Log" value={TABS.ACTIVITY_LOG} />
                    </OnwardTabContainer>
                </Grid>
                {/* Balances the breadcrumb column so the tabs stay centered. */}
                <Grid
                    item
                    css={css`
                        flex: 1;
                    `}
                />
            </Grid>
            {tab === TABS.DETAILS && !gridReady && (
                <Grid
                    container
                    justifyContent="center"
                    css={css`
                        padding: 2rem;
                    `}
                >
                    <CircularProgress aria-label="Loading manifest" />
                </Grid>
            )}
            {tab === TABS.DETAILS && gridReady && (
                <Grid
                    container
                    direction="column"
                    css={css`
                        padding: 2rem max(12.5%, 6rem);
                    `}
                >
                    <ManifestGrid
                        manifest={state.manifest}
                        orders={gridOrders}
                        initialSearchTerm={seedTerm}
                        editable={state.editable}
                        callbacks={callbacks}
                        pickBlockers={state.pickBlockers}
                    />
                </Grid>
            )}
            {tab === TABS.SURVEY && <Survey />}
            {tab === TABS.ACTIVITY_LOG && (
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        min-height: 0;
                        padding: 2rem max(12.5%, 6rem);
                    `}
                >
                    <Grid
                        container
                        css={css`
                            flex: 1;
                            min-height: 480px;
                        `}
                    >
                        <ActivityLog />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default compose(withContext)(Manifest);
